import {
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInYears,
  format,
  formatDistanceToNow,
  isBefore,
  isValid,
  max,
  min,
  parse,
  parseISO,
} from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { toZonedTime } from "date-fns-tz";

const handleStopPropagation = (e) => {
  e.preventDefault();
  e.stopPropagation();
};
const SITE_NAME = "HubHouse";
const setPageTitle = (titleKey, t) => {
  document.title = `${t(titleKey)} | ${t(SITE_NAME)}`;
};

const formatDate = (date, formatStr = "yyyy MMM dd") => {
  try {
    // Return "-" if the date is falsy (e.g., null, undefined, or empty)
    if (!date) {
      return "-";
    }
    // Parse the date string into a Date object
    const parsedDate = parseISO(date);

    // Check if the parsed date is valid
    if (isValid(parsedDate)) {
      return format(parsedDate, formatStr);
    }
    return "-";
  } catch (error) {
    console.error("Error formatting date:", error);
    return "-";
  }
};

const formatTime = (time) => {
  if (!time || typeof time !== "string") return ""; // Ensure `time` is a valid string

  try {
    // Parse the time string (e.g., "13:00") into a date object
    const parsedTime = parse(time, "HH:mm", new Date());

    // Check if the parsed time is valid
    if (isNaN(parsedTime)) {
      console.error("Invalid time value:", time);
      return ""; // Return an empty string for invalid time
    }

    // Format the time to 12-hour format with AM/PM
    return format(parsedTime, "h:mm a");
  } catch (error) {
    console.error("Error formatting time:", error);
    return ""; // Return an empty string on error
  }
};

const getTimeDifference = (start, end) => {
  try {
    if (!start || !end) return "-";

    // Parse time strings correctly (12-hour format with AM/PM)
    const startTime = parse(start, "hh:mm a", new Date());
    const endTime = parse(end, "hh:mm a", new Date());

    // Validate if parsed times are correct
    if (!isValid(startTime) || !isValid(endTime)) {
      return "-";
    }

    // Get the difference in minutes
    const diffMinutes = differenceInMinutes(endTime, startTime);

    // Convert minutes to hours and round to 1 decimal place
    return parseFloat((diffMinutes / 60).toFixed(1));
  } catch (error) {
    return "-";
  }
};

const getRemainingDaysForWorkshop = (selectedDateAndTimes) => {
  if (!selectedDateAndTimes || selectedDateAndTimes.length === 0)
    return "No Date Available";

  // Get the first date from the array
  const firstDate = selectedDateAndTimes[0]?.startTime?.date;

  if (!firstDate) return "Invalid Date";

  // Parse the date
  const parsedDate = parseISO(firstDate);

  // Validate the parsed date
  if (!isValid(parsedDate)) return "Invalid Date";

  // Calculate the difference in days
  const remainingDays = differenceInDays(parsedDate, new Date());

  return remainingDays > 0
    ? `${remainingDays} Day${remainingDays > 1 ? "s" : ""} Left!`
    : "Expired!";
};

const convertTo24HourFormat = (time) => {
  let { hours, minutes, period } = time;
  let hours24 = period === "PM" && hours !== 12 ? hours + 12 : hours;
  hours24 = period === "AM" && hours === 12 ? 0 : hours24;
  return `${String(hours24).padStart(2, "0")}:${String(minutes).padStart(
    2,
    "0"
  )}`;
};

function getMinMaxDate(dates = null) {
  if (!dates || dates.length === 0) return {};

  // Filter out null, undefined, and invalid dates
  const validDates = dates.filter((d) => d instanceof Date && isValid(d));

  if (validDates.length === 0) return {};

  if (validDates.length === 1) {
    const formattedDate = format(validDates[0], "yyyy-MM-dd");
    return {
      min: formattedDate,
      max: formattedDate,
    };
  }

  const minDate = min(validDates);
  const maxDate = max(validDates);

  return {
    min: format(minDate, "yyyy-MM-dd"),
    max: format(maxDate, "yyyy-MM-dd"),
  };
}

const getFormatDates = (dates) => {
  if (dates && dates.length === 2) {
    return {
      min: format(dates[0], "yyyy-MM-dd"),
      max: format(dates[1], "yyyy-MM-dd"),
    };
  }
  return {};
};

const getTimePart = (time, type) => {
  if (!time) return "";
  const parsed = parse(time, "HH:mm", new Date());
  return format(parsed, type);
};

const formatFilterOptions = (list, nameKey = "name", valueKey = "_id") => {
  return (
    list?.map((item) => ({
      name: item[nameKey] || "-",
      value: item[valueKey],
    })) || []
  );
};

const convertTo12HourObject = (time) => {
  if (!time) return { hours: 12, minutes: 0, period: "AM" };

  try {
    const parsed = parse(time, "HH:mm", new Date());

    return {
      hours: Number(format(parsed, "h")), // 12-hour format (no leading zero)
      minutes: parsed.getMinutes(),
      period: format(parsed, "a"), // AM / PM
    };
  } catch {
    return { hours: 12, minutes: 0, period: "AM" }; // fallback
  }
};

const getKeywordValue = (actionType, keyword) => {
  return ["RESET", "RESET_KEYWORD"].includes(actionType) ? "" : keyword || "";
};

const getRelativeTimeFromNow = (dateString, timeZone = "Asia/Riyadh") => {
  const date = new Date(dateString);

  if (isNaN(date.getTime()) || !timeZone) {
    return "-";
  }

  const zonedDate = toZonedTime(date, timeZone);
  const nowZoned = toZonedTime(new Date(), timeZone);
  const hoursDiff = differenceInHours(nowZoned, zonedDate);

  return hoursDiff < 24
    ? formatDistanceToNow(zonedDate, { addSuffix: true })
    : format(zonedDate, "MMM dd, yyyy");
};

const formatDateTime = (date) => {
  if (!date || !isValid(date)) return "";
  return format(date, "yyyy MMM dd, hh:mmaaa").toLowerCase();
};

const convertToUTCDateTimeString = (date) => {
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return null;
  }

  // Format directly in UTC timezone
  return formatInTimeZone(date, "UTC", "yyyy-MM-dd'T'HH:mm:ss'Z'");
};

const getAgeFromDOB = (dobString) => {
  if (!dobString) return 0;

  try {
    let dob;

    if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(dobString)) {
      // yyyy-MM-dd
      dob = parse(dobString, "yyyy-MM-dd", new Date());
    } else if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(dobString)) {
      // d-M-yyyy or dd-MM-yyyy
      dob = parse(dobString, "d-M-yyyy", new Date());
    } else {
      console.error("Unsupported date format:", dobString);
      return 0;
    }

    const age = differenceInYears(new Date(), dob);
    return age >= 0 ? age : 0;
  } catch (error) {
    console.error("Failed to parse DOB:", dobString, error);
    return 0;
  }
};

const isExpired = (expiresAt) => {
  // Parse the input date string into a Date object
  const expiryDate = parse(expiresAt, "yyyy/M/d", new Date());

  // Get the current date
  const now = new Date();

  // Check if the current date is after the expiry date
  return isBefore(expiryDate, now);
};

const formatBookingDateAndTime = (bookingDate) => {
  if (!bookingDate) return "";
  return formatInTimeZone(
    bookingDate,
    "UTC",
    "yyyy MMM dd, hh:mmaaa"
  ).toLowerCase();
};

const formatToAmPm = (timeStr) => {
  if (!timeStr) return "";
  try {
    const parsedTime = parse(timeStr, "HH:mm", new Date());
    return format(parsedTime, "hh:mm a");
  } catch (error) {
    console.error("Invalid time string:", timeStr);
    return "";
  }
};

const formatToCustomDateTime = (date) => {
  const parsedDate = typeof date === "string" ? parseISO(date) : date;
  if (!parsedDate || !isValid(parsedDate)) return "";
  return format(parsedDate, "dd MMMM yyyy – h:mm a");
};

const toNumber = (value) => Number(value) || 0;

export {
  handleStopPropagation,
  formatDate,
  formatTime,
  getTimeDifference,
  getRemainingDaysForWorkshop,
  convertTo24HourFormat,
  getMinMaxDate,
  getFormatDates,
  formatFilterOptions,
  getKeywordValue,
  getTimePart,
  convertTo12HourObject,
  getRelativeTimeFromNow,
  formatDateTime,
  convertToUTCDateTimeString,
  getAgeFromDOB,
  isExpired,
  formatBookingDateAndTime,
  formatToAmPm,
  formatToCustomDateTime,
  toNumber,
  setPageTitle
};
